// Pagiflow.ts

import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ViewEncapsulation,
  Inject,
  PLATFORM_ID,
  NgZone,
  Renderer2,
  HostBinding,
} from '@angular/core';
// import Pagiflow from 'pagiflow';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { PagiflowInstance, PagiflowOptions } from './types';

// import Pagiflow from 'pagiflow'; // Assuming pagiflow is available as a global or via import

// type PagiflowCtor = (
//   selector: string | HTMLElement,
//   opts?: PagiflowOptions
// ) => PagiflowInstance;

// // ─── Pagiflow loader ──────────────────────────────────────────────────────────
// function getPagiflow(isBrowser: boolean): PagiflowCtor | null {
//   if (!isBrowser) return null;
//   const ctor = (window as any).Pagiflow || Pagiflow;
//   return ctor as PagiflowCtor;
// }

type PagiflowCtor = (
  selector: string | HTMLElement,
  opts?: PagiflowOptions
) => PagiflowInstance;

function getPagiflow(): PagiflowCtor | null {
  if (typeof window !== 'undefined' && (window as any).Pagiflow) {
    return (window as any).Pagiflow as PagiflowCtor;
  }
  try {
    const mod = require('pagiflow');
    return (mod.default ?? mod) as PagiflowCtor;
  } catch {
    return null;
  }
}


function initPagiflowFromElement(
  ctor: PagiflowCtor,
  el: HTMLElement,
  opts: PagiflowOptions,
  renderer: Renderer2
): PagiflowInstance | null {
  const attrValue = `pf-${Math.random().toString(36).slice(2)}`;
  const selector = `[data-pagiflow-angular-id="${attrValue}"]`;
  renderer.setAttribute(el, 'data-pagiflow-angular-id', attrValue);

  const bySelector = ctor(selector, opts) as PagiflowInstance | null;
  if (!bySelector) {
    renderer.removeAttribute(el, 'data-pagiflow-angular-id');
    return null;
  }
  return bySelector;
}

// ─── Pagiflow Component ───────────────────────────────────────────────────────
@Component({
  selector: 'pf-pagiflow',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      #track
      [ngClass]="['pf-track', className || '']"
      [style]="style"
    >
      <ng-content></ng-content>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class PagiflowComponent implements AfterViewInit, OnDestroy {
  @ViewChild('track', { read: ElementRef }) trackRef!: ElementRef<HTMLDivElement>;

  // Input options
  @Input() options: PagiflowOptions = {};
  @Input() className?: string;
  @Input() style?: Record<string, string | number>;

  // Output events
  @Output() slideChange = new EventEmitter<number>();
  @Output() ready = new EventEmitter<PagiflowInstance>();

  private instance: PagiflowInstance | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private raf1 = 0;
  private raf2 = 0;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    this.initPagiflow();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private initPagiflow(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const el = this.trackRef.nativeElement;
    const existing = (el as any).pagiflowInstance as PagiflowInstance | undefined;

    let inst: PagiflowInstance | null = null;

    if (existing && !existing.destroyed) {
      inst = existing;
    } else {
      const ctor = getPagiflow();
      if (!ctor) {
        console.error('[PagiflowComponent] Pagiflow core not found. Install pagiflow or load it via CDN.');
        return;
      }
      inst = initPagiflowFromElement(ctor, el, this.options, this.renderer);
    }

    if (!inst) return;

    this.instance = inst;
    (el as any).pagiflowInstance = inst;
    this.ready.emit(inst);

    // Listen to slide changes via the instance API
    inst.onSlideChange((idx) => {
      this.ngZone.run(() => {
        this.slideChange.emit(idx);
      });
    });

    // Setup resize observer for responsive behavior
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.raf1);
        this.raf1 = requestAnimationFrame(() => {
          cancelAnimationFrame(this.raf2);
          this.raf2 = requestAnimationFrame(() => {
            if (this.instance) this.instance.reInit();
          });
        });
      });
      this.resizeObserver.observe(el);
    });
  }

  private destroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    cancelAnimationFrame(this.raf1);
    cancelAnimationFrame(this.raf2);

    if (this.instance && !this.instance.destroyed) {
      this.instance.destroy();
    }
    this.instance = null;
  }

  // Public API methods

  /**
   * Get the underlying Pagiflow instance.
   */
  getInstance(): PagiflowInstance | null {
    return this.instance;
  }

  /**
   * Navigate to the next slide.
   */
  next(): void {
    this.instance?.next();
  }

  /**
   * Navigate to the previous slide.
   */
  prev(): void {
    this.instance?.prev();
  }

  /**
   * Go to a specific slide index.
   */
  goTo(index: number, opts?: { silent?: boolean; instant?: boolean }): void {
    this.instance?.goTo(index, opts);
  }

  /**
   * Start autoScroll.
   */
  play(): void {
    this.instance?.play();
  }

  /**
   * Pause autoScroll.
   */
  pause(): void {
    this.instance?.pause();
  }

  /**
   * Resume autoScroll after pause.
   */
  resume(): void {
    this.instance?.resume();
  }

  /**
   * Toggle autoScroll play/pause state.
   */
  togglePlayPause(): void {
    this.instance?.togglePlayPause();
  }

  /**
   * Update options and optionally rebuild.
   */
  setOptions(opts: Partial<PagiflowOptions>, rebuild?: boolean): void {
    this.instance?.setOptions(opts, rebuild);
  }

  /**
   * Reinitialize with new or updated options.
   */
  reInit(opts?: Partial<PagiflowOptions>): void {
    this.instance?.reInit(opts);
  }
}

// ─── Pagiflow Slide Component ─────────────────────────────────────────────────
@Component({
  selector: 'pf-slide',
  standalone: true,
  template: `<ng-content></ng-content>`,
  host: {
    'class': 'pf-slide',
    '[class]': 'className',
    '[style]': 'style'
  },
  encapsulation: ViewEncapsulation.None,
})
export class PagiflowSlideComponent {
  @Input() className?: string;
  @Input() style?: string | Record<string, string | number>;
}
